import { useState } from 'react'

import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import { useDecision } from '@optimizely/react-sdk'
import { invalidateCommunityQueryEventDispatch } from '@space-lib-events/invalidateCommunityQuery'
import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import Button from '@space-lib/components/button'
import Icon from '@space-lib/components/icon'

import Modal from '@space-lib/components/modal'
import ModalBody from '@space-lib/components/modalBody'
import ModalFooter from '@space-lib/components/modalFooter'
import ModalHeader from '@space-lib/components/modalHeader'
import Tooltip from '@space-lib/components/tooltip'
import { Actions, Entities, EventNames, ScreenNames, RequestResults } from '@space-lib/datahub'
import useTranslation from '@space-lib/hooks/useTranslation'
import { ThemeMode } from '@space-lib/styles/themes/enum'
import DefaultImageCourseDark from '@space-shell/assets/images/defaultImageCourseDark.svg'
import DefaultImageCourseLight from '@space-shell/assets/images/defaultImageCourseLight.svg'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import { BIND_EXCLUSIVE_COMMUNITY_SURVEY } from '@space-shell/core/store'
import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'
import { useAssociateOrDissociateCommunity } from '@space-shell/modules/communities/hooks/useAssociateOrDissociateCommunity'
import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'

import { CourseName, CustomButton, CustomImage } from './styles'
import type { CourseItemsProps } from './types'

const CourseItems = ({
  backgroundImageFinal,
  hasCommunityAssociated,
  description,
  productName,
  idProduct,
  numberOfAssociatedProducts
}: CourseItemsProps) => {
  const { sendDataHubConsume } = useSendDataHubConsume()
  const { theme } = useThemeContext()
  const { t } = useTranslation()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const { associateOrDissociateCommunity, invalidateQueries } = useAssociateOrDissociateCommunity()
  const { community } = useCommunityConfigurationSidebar()

  const [{ enabled: isEnableExclusiveCommunityBindSurveyModal }] = useDecision(
    'com_adm_web_enable_exclusive_community_bind_survey_modal'
  )

  const [{ enabled: isEnabledChangeCommunityType }] = useDecision(
    'com_adm_web_enable_change_community_type'
  )

  const isCommunityLocked = isEnabledChangeCommunityType && community.isLocked

  const shouldSetOpenSurveyModalFlag =
    isEnableExclusiveCommunityBindSurveyModal &&
    numberOfAssociatedProducts === 0 &&
    !hasCommunityAssociated

  const closeModal = () => {
    setIsOpenModal(false)
  }

  const imageByThemeMode =
    theme === ThemeMode.DARK ? DefaultImageCourseDark : DefaultImageCourseLight

  const handleAssociateOrDissociateCommunity = async () => {
    associateOrDissociateCommunity.mutate(
      {
        body: {
          collectionCourses: [
            {
              idCollection: community.id,
              idCourse: idProduct,
              bind: !hasCommunityAssociated
            }
          ]
        }
      },
      {
        onSuccess: async () => {
          await invalidateQueries()

          setIsOpenModal(false)

          toastEventDispatch({
            type: ToastType.SUCCESS,
            message: !hasCommunityAssociated
              ? t('configuration_sidebar.access_and_privacy.toast.success')
              : t('configuration_sidebar.access_and_privacy.toast.success_disassociate'),
            position: ToastPosition.BOTTOM_RIGHT
          })

          sendDataHubConsume({
            entity: Entities.COMMUNITY,
            action: Actions.BIND,
            event: {
              eventName: !hasCommunityAssociated
                ? EventNames.TRY_TO_SAVE_PRODUCT_BIND_CHANGES
                : EventNames.TRY_TO_SAVE_PRODUCT_UNBIND_CHANGES,
              screenName: ScreenNames.COMMUNITY_CONFIGURATION_MODAL_ACCESS_AND_PRIVACY,
              result: RequestResults.SUCCESS,
              communityId: community.id,
              communityName: community.name,
              productId: idProduct,
              productName
            }
          })

          const storedData = JSON.parse(
            window.localStorage.getItem(BIND_EXCLUSIVE_COMMUNITY_SURVEY) || '{}'
          )
          const { surveyCompleted } = storedData

          if (shouldSetOpenSurveyModalFlag) {
            window.localStorage.setItem(
              BIND_EXCLUSIVE_COMMUNITY_SURVEY,
              JSON.stringify({ showModal: true, surveyCompleted })
            )
          }

          invalidateCommunityQueryEventDispatch()
        },
        onError(err) {
          toastEventDispatch({
            type: ToastType.DANGER,
            message: !hasCommunityAssociated
              ? t('configuration_sidebar.access_and_privacy.toast.error')
              : t('configuration_sidebar.access_and_privacy.toast.error_disassociate'),
            position: ToastPosition.BOTTOM_RIGHT
          })

          sendDataHubConsume({
            entity: Entities.COMMUNITY,
            action: Actions.BIND,
            event: {
              eventName: !hasCommunityAssociated
                ? EventNames.TRY_TO_SAVE_PRODUCT_BIND_CHANGES
                : EventNames.TRY_TO_SAVE_PRODUCT_UNBIND_CHANGES,
              screenName: ScreenNames.COMMUNITY_CONFIGURATION_MODAL_ACCESS_AND_PRIVACY,
              result: RequestResults.FAILURE,
              failureKind: err.message,
              productId: idProduct,
              productName,
              communityId: community.id,
              communityName: community.name
            }
          })
        }
      }
    )
  }

  const handleClickButton = () => {
    if (!hasCommunityAssociated) {
      sendDataHubConsume({
        entity: Entities.COMMUNITY,
        action: Actions.BIND,
        event: {
          eventName: EventNames.BIND_A_PRODUCT_TO_COMMUNITY,
          screenName: ScreenNames.COMMUNITY_CONFIGURATION_MODAL_ACCESS_AND_PRIVACY,
          communityId: community.id,
          communityName: community.name,
          productId: idProduct,
          productName
        }
      })
      handleAssociateOrDissociateCommunity()
    } else {
      sendDataHubConsume({
        entity: Entities.COMMUNITY,
        action: Actions.BIND,
        event: {
          eventName: EventNames.UNBIND_A_PRODUCT_TO_COMMUNITY,
          screenName: ScreenNames.COMMUNITY_CONFIGURATION_MODAL_ACCESS_AND_PRIVACY,
          communityId: community.id,
          communityName: community.name,
          productId: idProduct,
          productName
        }
      })
      setIsOpenModal(true)
    }
  }

  const handleClickConfirmOption = () => {
    sendDataHubConsume({
      entity: Entities.COMMUNITY,
      action: Actions.BIND,
      event: {
        eventName: EventNames.CONFIRM_UNBIND_A_PRODUCT_TO_COMMUNITY,
        screenName: ScreenNames.COMMUNITY_CONFIGURATION_MODAL_ACCESS_AND_PRIVACY,
        communityId: community.id,
        communityName: community.name,
        productId: idProduct,
        productName
      }
    })

    handleAssociateOrDissociateCommunity()
  }

  return (
    <>
      <div
        data-test='list-courses-community-config'
        className='_w-full _d-flex _align-items-center card-container _py-4 _pr-sm-5 _pl-sm-2'>
        <div className='_d-flex _align-items-center card-container'>
          <CustomImage
            loading='lazy'
            src={backgroundImageFinal || imageByThemeMode}
            alt={description}
          />
          <div className='_d-flex _flex-column _ml-3'>
            <CourseName className='_text-2 _mb-0 _font-weight-light _text-gray-900'>
              {productName}
            </CourseName>
            <p className='_mb-0 _font-weight _text-1 _text-gray-900'>{`ID ${idProduct}`}</p>
          </div>
        </div>
        <Tooltip
          content={t(
            `${
              isCommunityLocked ? 'configuration_sidebar.change_community_type_tooltip_alert' : ''
            }`
          )}
          position='top'>
          <CustomButton
            $hasCommunityAssociated={hasCommunityAssociated}
            variation='tertiary'
            disabled={isCommunityLocked}
            className='_text-2 _font-weight-light'
            onClick={() => handleClickButton()}
            loading={associateOrDissociateCommunity.isLoading}>
            {!hasCommunityAssociated
              ? t('configuration_sidebar.access_and_privacy.associate')
              : t('configuration_sidebar.access_and_privacy.disassociate')}
          </CustomButton>
        </Tooltip>
      </div>
      <Modal show={isOpenModal} onClose={closeModal}>
        <ModalHeader className='_px-5 _pt-5 _pb-4'>
          <div className='_d-flex _align-items-baseline'>
            <h2 className='_text-4 _m-0 _mr-4 _font-weight-light'>
              {t('configuration_sidebar.access_and_privacy.modal_disassociation_course.title')}
            </h2>
            <div className='_cursor-pointer'>
              <Icon
                data-test='close-icon-modal-course-items'
                icon={faXmark}
                onClick={closeModal}
                className='_w-5 _h-5'
              />
            </div>
          </div>
        </ModalHeader>
        <ModalBody className='_p-0 _px-5'>
          <p className='_text-2 _text-gray-500 _font-weight _m-0'>
            {numberOfAssociatedProducts === 1
              ? t(
                  'configuration_sidebar.access_and_privacy.modal_disassociation_course.description_v2'
                )
              : t(
                  'configuration_sidebar.access_and_privacy.modal_disassociation_course.description'
                )}
          </p>
        </ModalBody>
        <ModalFooter className='_px-5 _pt-8 _pb-5'>
          <div className='_d-flex _ml-auto'>
            <Button
              disabled={associateOrDissociateCommunity.isLoading}
              className='_mr-5'
              variation='default'
              onClick={closeModal}>
              {t('configuration_sidebar.access_and_privacy.modal_disassociation_course.cancel')}
            </Button>
            <Button
              variation='primary'
              loading={associateOrDissociateCommunity.isLoading}
              onClick={handleClickConfirmOption}>
              {t('configuration_sidebar.access_and_privacy.modal_disassociation_course.confirm')}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CourseItems
